import { fetchJsonWithAuth } from "./authApi";
import { AUTH_URL } from "./apiConfig";

const unwrap = (json) => json?.data ?? json;

/**
 * Converts a backend "KV array" candidate — [{Key, Value}, {Key, Value}, ...] —
 * into a normal flat object { Key: Value, ... }.
 */
function flattenKvItem(item) {
  if (Array.isArray(item) && item.every(p => p && typeof p === 'object' && 'Key' in p)) {
    return item.reduce((acc, { Key, Value }) => {
      acc[Key] = Value;
      return acc;
    }, {});
  }
  return item;
}

export function extractStageCandidates(stage) {
  if (!stage) return [];
  if (Array.isArray(stage.data)) return stage.data.map(flattenKvItem);
  if (stage.stage && Array.isArray(stage.stage.data)) return stage.stage.data.map(flattenKvItem);
  if (Array.isArray(stage)) return stage.map(flattenKvItem);
  return [];
}

export async function getWorkflowByEntity(entityId) {
  try {
    const json = await fetchJsonWithAuth(
      AUTH_URL,
      `/workflow/entity/${entityId}?workflowType=HiringPipeline`
    );
    const data = unwrap(json);
    return data ?? null;
  } catch (error) {
    if (error?.status === 404 || error?.message?.includes('404')) {
      return null;
    }
    throw error;
  }
}

/**
 * GET /workflow/list/all — workflows across ALL modules for the logged-in
 * user (no module param; tenant/business scoping comes from the JWT).
 * Server-paginated: page starts at 1, limit max 100.
 * Returns { items, total, page, limit } — items is the slim workflow list
 * (_id, module, workflowType, entityType, entityId, currentStage, totalCount,
 * stages [stageId/stageName/sequence/count], timestamps).
 */
export async function getAllWorkflows(page = 1, limit = 10) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/list/all?page=${page}&limit=${limit}`
  );
  const data = unwrap(json);
  const items = Array.isArray(data) ? data : data?.data ?? [];
  const total = Number(json?.total ?? data?.total) || items.length;
  return { items, total, page, limit };
}

/**
 * GET /workflow/list?module=Jobs&page=1&limit=10 — ONE server-paginated page
 * of workflows for a single module (page starts at 1, limit max 100; the
 * module name must match the workflow documents exactly, e.g. "Jobs" not
 * "job"). This is the primary (driver) source for the server-paginated
 * merged Jobs list — never fetch the full set for that path.
 * Returns { items, total, page, limit }.
 */
export async function getWorkflowsByModule(module, page = 1, limit = 10) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/list?module=${encodeURIComponent(module)}&page=${page}&limit=${limit}`
  );
  const data = unwrap(json);
  const items = Array.isArray(data) ? data : data?.data ?? [];
  const total = Number(json?.total ?? data?.total) || items.length;
  return { items, total, page, limit };
}

/**
 * Fetches every page of /workflow/list/all and returns the full set —
 * the shape the merge engine needs for its primary (driver) source.
 * DEPRECATED for list rendering: use getWorkflowsByModule (server-paginated)
 * via createAllWorkflowsListFetcher instead of fetching the whole collection.
 */
export async function getAllWorkflowsComplete(pageSize = 100) {
  const first = await getAllWorkflows(1, pageSize);
  const items = [...first.items];
  const total = first.total;

  let page = 2;
  while (items.length < total) {
    const next = await getAllWorkflows(page, pageSize);
    if (!next.items.length) break; // guard against a stale/overstated total
    items.push(...next.items);
    page += 1;
  }

  return { items, total: Math.max(total, items.length) };
}

export async function getWorkflowById(workflowId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}`);
  return unwrap(json);
}

export async function getWorkflowStage(workflowId, stageName) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/${workflowId}/stage/${stageName}`
  );
  let result = unwrap(json);
  if (Array.isArray(result)) {
    return { data: result };
  }
  return result;
}

/**
 * Get a single module record by id via the /module-data-list gateway
 * (same endpoint used to list Jobs), filtered down to one id.
 * Used as the per-id fallback in the merged-list fetchers.
 *
 * The id is sent through the `filters` mechanism (an `_id in [id]`
 * FilterCondition) — the backend does not read a bare `id` query param, so
 * the old `&id=` form silently returned the module's first record.
 * scope=allJobs widens the job scope so records outside "My Jobs" still
 * resolve; non-job modules ignore the scope param entirely.
 */
export async function getModuleRecordById(moduleName, id) {
  if (!moduleName || !id) return null;

  try {
    const params = new URLSearchParams({
      module: moduleName,
      limit: '1',
      offset: '0',
      scope: 'allJobs',
      filters: JSON.stringify([{ field: '_id', op: 'in', values: [String(id)], operator: 'and' }]),
    });
    const json = await fetchJsonWithAuth(AUTH_URL, `/module-data-list?${params}`);
    const data = unwrap(json);

    if (Array.isArray(data)) return data[0] ?? null;
    if (data?.data && Array.isArray(data.data)) return data.data[0] ?? null;
    const items = data?.rows ?? data?.records ?? data?.items ?? data?.list;
    if (Array.isArray(items)) return items[0] ?? null;
    return data ?? null;
  } catch (err) {
    console.error(`[getModuleRecordById] failed: ${moduleName}/${id}`, err);
    return null;
  }
}

/**
 * Get a single candidate's full profile by reference ID.
 */
export async function getCandidateByReferenceId(referenceId) {
  return getModuleRecordById("candidate", referenceId);
}

/**
 * Get a single submission's full record by submission ID.
 * Submission-level fields (pay rate, job, internal/client status, submitted by)
 * live on the submissions collection — NOT on the candidate record — so this
 * is a separate call from getCandidateByReferenceId.
 */
export async function getSubmissionById(submissionId) {
  return getModuleRecordById("submissions", submissionId);
}

export async function createWorkflow(payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, "/workflow/create", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return unwrap(json);
}

export async function updateWorkflowStage(workflowId, payload) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}/stage`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
  return unwrap(json);
}

export async function getWorkflowDashboard(module) {
  const json = await fetchJsonWithAuth(
    AUTH_URL,
    `/workflow/dashboard?module=${module}&workflowType=HiringPipeline`
  );
  return unwrap(json);
}

// The admin-configurable list of hiring-pipeline stage names (Pipeline,
// Shortlisted, Submitted, …), aggregated live across every job's workflow —
// the DB source of truth for stage values shown in Tag to Jobs / resolved by
// the Shortlist action, instead of a hardcoded options list.
export async function getHiringStageOptions() {
  const dashboard = await getWorkflowDashboard('job');
  const stages = dashboard?.stages ?? dashboard?.Stages ?? [];
  return stages
    .map((stage) => stage?.stageName ?? stage?.StageName)
    .filter(Boolean)
    .map((stageName) => ({ label: stageName, value: stageName }));
}

// Resolves the DB-configured stage option matching "shortlisted" (case
// insensitive) — never hardcode the raw string where this value is consumed.
export async function getShortlistedStageValue() {
  const options = await getHiringStageOptions();
  const match = options.find((option) => option.value.trim().toLowerCase() === 'shortlisted');
  return match?.value ?? null;
}

export async function deleteWorkflow(workflowId) {
  const json = await fetchJsonWithAuth(AUTH_URL, `/workflow/${workflowId}`, {
    method: "DELETE",
  });
  return unwrap(json);
}
